import Util from './util';
import Auth from './auth';
import Logger from '@utils/logger';
import Constants from './AppConstants';

interface PostMessageParams {
  inputText: string;
  optionsDetails: any;
  conversationId: string;
  shouldCreateProcess: boolean;
  getComponents: boolean;
  shouldCreateModel: boolean;
  updatedDAG?: string;
  signal?: any;
}

const apiService = {
  // Load intents from the flat file
  getPreDefinedIntents() {
    const getDefaultIntents = () => this.getIntents();
    if (Auth.isJwtValid()) {
      return getDefaultIntents();
    }

    return Auth.obtainJWTToken()
      .then((jwt) => {
        if (jwt) {
          Util.setCookie('JWT', jwt);
          return getDefaultIntents();
        }
        return Promise.resolve();
      })
      .catch((error) => error);
  },
  // Get the predefined intents from S3
  getIntents() {
    const jwt = Util.getCookie('JWT');
    const apiUrl = `${Util.getAWSHost()}/autocomplete`;
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `${jwt}`,
    };

    return new Promise((resolve, reject) => {
      fetch(apiUrl, {
        method: 'POST',
        headers,
      })
        .then((response) => {
          if (response.ok) {
            response.json().then((data) => {
              resolve(data);
            });
          } else {
            // eslint-disable-next-line
            console.log('res', response);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  getApi(handler: string, handlerActionClass: string, payload: object) {
    const host = Util.getBackendHost();
    const accountId = Util.getCookie('ACCOUNT_ID');
    const jwt = Util.getCookie('JWT');
    const featureName = Constants.AI_TNC;
    const visibility = Constants.ENABLED;

    const API_URL = `${host}/restrpc/account/${accountId}/handler/${handler}`;

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
    };

    const finalPayload = {
      ...payload,
      featureName,
      visibility,
      accountId,
    };

    return new Promise((resolve, reject) => {
      fetch(API_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify(finalPayload),
      })
        .then((response) => {
          response.json().then((data) => {
            const resData = { ...data, status: response.status };
            resolve(resData);
          });
        })
        .catch((error) => reject(error));
    });
  },

  get(handler: string, handlerActionClass: string, payload: object) {
    const getApiCall = () => this.getApi(handler, handlerActionClass, payload);
    if (Auth.isJwtValid()) {
      return getApiCall();
    }
    return Auth.obtainJWTToken()
      .then((jwt) => {
        if (jwt) {
          Util.setCookie('JWT', jwt);
          return getApiCall();
        }
        Logger.warn('No JWT Token Found');
        return Promise.resolve();
      })
      .catch(() => {
        Logger.error('Error occured while refreshing the JWT');
        Promise.reject();
      });
  },

  postFeedback(
    timeStamp: any,
    voting: any,
    labelArray: string[],
    textResponse: string,
  ) {
    const jwt = Util.getCookie('JWT');
    const apiUrl = `${Util.getAWSHost()}/feedback`;
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `${jwt}`,
    };
    const payload = {
      response_timestamp: timeStamp,
      attributes: {
        vote: voting,
        comments: {
          labels: labelArray,
          text: textResponse,
        },
      },
    };
    return new Promise((resolve) => {
      fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      })
        .then((response) => {
          response.json().then((data) => {
            const resData = { ...data, status: response.status };
            resolve(resData);
          });
        })
        .catch((error) => error);
    });
  },
  feedBackResponseCall(
    timeStamp: any,
    voting: any,
    labelArray: string[],
    textResponse: string,
  ) {
    const postFeedBackCall = () => this.postFeedback(timeStamp, voting, labelArray, textResponse);
    if (Auth.isJwtValid()) {
      return postFeedBackCall();
    }
    return Auth.obtainJWTToken()
      .then((jwt) => {
        if (jwt) {
          Util.setCookie('JWT', jwt);
          return postFeedBackCall();
        }
        Logger.warn('No JWT Token Found');
        return Promise.resolve();
      })
      .catch(() => {
        Logger.error('Error occured while refreshing the JWT');
        Promise.reject();
      });
  },

  getProcessDocument(component_id: any, conversationId: any, context: string) {
    const jwt = Util.getCookie('JWT');
    const apiUrl = `${Util.getAWSHost()}/description`;
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `${jwt}`,
    };
    const payload = {
      process_id: component_id,
      tag: context,
      conversation_id: '',
    };
    return new Promise((resolve) => {
      fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      })
        .then((response) => {
          response.json().then((data) => {
            const resData = { ...data, status: response.status };
            resolve(resData);
          });
        })
        .catch((error) => error);
    });
  },
  processDocumentation(
    component_id: any,
    conversation_id: any,
    context: string,
  ) {
    const processDocument = () => this.getProcessDocument(component_id, conversation_id, context);
    if (Auth.isJwtValid()) {
      return processDocument();
    }
    return Auth.obtainJWTToken()
      .then((jwt) => {
        if (jwt) {
          Util.setCookie('JWT', jwt);
          return processDocument();
        }
        Logger.warn('No JWT Token Found');
        return Promise.resolve();
      })
      .catch(() => {
        Logger.error('Error occured while refreshing the JWT');
        Promise.reject();
      });
  },
  getConnections(Keyword: any, connectorType: any, token: any) {
    const jwt = Util.getCookie('JWT');
    const apiUrl = `${Util.getAWSHost()}/component/query`;
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `${jwt}`,
    };
    const payload = {
      filterKeyword: Keyword,
      type: 'connection',
      connectorType,
      queryToken: token,
    };
    return new Promise((resolve, reject) => {
      fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      }).then((response) => {
        response.json().then((data) => {
          const resData = { ...data, status: response.status };
          resolve(resData);
        });
      })
        .catch((error) => reject(error));
    });
  },
  getConnectionsForConnectorType(keyword: any, connectorType: any, token: any) {
    const connections = () => this.getConnections(keyword, connectorType, token);
    if (Auth.isJwtValid()) {
      return connections();
    }
    return Auth.obtainJWTToken()
      .then((jwt) => {
        if (jwt) {
          Util.setCookie('JWT', jwt);
          return connections();
        }
        Logger.warn('No JWT Token Found');
        return Promise.resolve();
      })
      .catch(() => {
        Logger.error('Error occured while refreshing the JWT');
        Promise.reject();
      });
  },
  checkUrlValidity(url: any, process: any) {
    const jwt = Util.getCookie('JWT');
    const apiUrl = `${Util.getAWSHost()}/component/validateUrl`;
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `${jwt}`,
    };
    const payload = {
      URL: url,
      type: process,
    };
    return new Promise((resolve) => {
      fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      }).then((response) => {
        response.json().then((data) => {
          const resData = { ...data, status: response.status };
          resolve(resData);
        });
      })
        .catch((error) => error);
    });
  },
  isValidUrl(url: any, process: any) {
    const isValidUrl = () => this.checkUrlValidity(url, process);
    if (Auth.isJwtValid()) {
      return isValidUrl();
    }
    return Auth.obtainJWTToken()
      .then((jwt) => {
        if (jwt) {
          Util.setCookie('JWT', jwt);
          return isValidUrl();
        }
        Logger.warn('No JWT Token Found');
        return Promise.resolve();
      })
      .catch(() => {
        Logger.error('Error occured while refreshing the JWT');
        Promise.reject();
      });
  },
};

export default apiService;
export function postApiCall(
  params: PostMessageParams,
) {
  const jwt = Util.getCookie('JWT');
  const apiUrl = `${Util.getAWSHost()}/chat`;
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `${jwt}`,
  };
  const {
    inputText,
    optionsDetails,
    conversationId,
    shouldCreateProcess,
    getComponents,
    shouldCreateModel,
    updatedDAG,
    signal,
  } = params;
  const payload = {
    prompt: inputText,
    options: optionsDetails,
    conversation_id: conversationId,
    create_process: shouldCreateProcess,
    get_components: getComponents,
    create_models: shouldCreateModel,
    ...(updatedDAG && { DAG: updatedDAG }),
  };

  return new Promise((resolve, reject) => {
    fetch(apiUrl, {
      method: 'POST',
      signal,
      headers,
      body: JSON.stringify(payload),
    })
      .then((response) => {
        response.json().then((data) => {
          const resData = { ...data, status: response.status };
          resolve(resData);
        });
      })
      .catch((error) => {
        reject(error);
      });
  });
}
export function postMessage(
  params: PostMessageParams,
) {
  const apiCall = () => postApiCall(
    params,
  );

  if (Auth.isJwtValid()) {
    return apiCall();
  }

  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        return apiCall();
      }
      return Promise.resolve();
    })
    .catch((error) => error);
}
export function getProcess(Keyword: any, process: any, token: any) {
  const jwt = Util.getCookie('JWT');
  const apiUrl = `${Util.getAWSHost()}/component/query`;
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `${jwt}`,
  };
  const payload = {
    filterKeyword: Keyword,
    type: process,
    queryToken: token,
  };
  return new Promise((resolve, reject) => {
    fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    }).then((response) => {
      response.json().then((data) => {
        const resData = { ...data, status: response.status };
        resolve(resData);
      });
    })
      .catch((error) => reject(error));
  });
}
export function getUserProcesses(keyword: any, process: any, token: any) {
  const processDocument = () => getProcess(keyword, process, token);
  if (Auth.isJwtValid()) {
    return processDocument();
  }
  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        return processDocument();
      }
      Logger.warn('No JWT Token Found');
      return Promise.resolve();
    })
    .catch(() => {
      Logger.error('Error occured while refreshing the JWT');
      Promise.reject();
    });
}
